// §31 free embeddable quote-request widget - shared request/response schemas
// and the QuoteDraftPrefill contract (I-31-1). Phase 31-A scope only: the
// domain payload shape and the contact validator (I-31-2). The anti-abuse
// transport fields (submissionId, honeypot, Turnstile token) that wrap this
// payload at the API boundary are Phase 31-B's concern (§31.4 #7), not
// stored domain data, so they live in the future route schema, not here.
import { z } from 'zod';
import { REMOVAL_TIERS, FINISH_FAMILIES, EMBED_PANEL_AREAS, EMBED_DISASSEMBLY_CHOICES, EMBED_CONDITION_CHOICES, MIN_VEHICLE_YEAR, maxVehicleYear, QUOTE_SERVICES, PPF_COVERAGE_CHOICES, PPF_FINISH_CHOICES, TINT_FILM_CHOICES, TINT_SHADE_CHOICES, TINT_AREA_CHOICES, TINT_EXISTING_CHOICES, CERAMIC_PACKAGE_CHOICES, CERAMIC_CORRECTION_CHOICES, CERAMIC_ADDON_CHOICES, PRINT_COVERAGE_CHOICES, PRINT_ARTWORK_CHOICES, } from '../constants.js';
import { uuidSchema } from './common.js';
// ---------------------------------------------------------------------------
// String-field hardening (P-31-3): every free-text field rejects control
// characters (subject-injection / header-injection guard, §31.4 anti-abuse
// item 6) in addition to a length cap. Scoped to this module rather than
// common.ts - no other schema in the codebase needs the control-char check.
// ---------------------------------------------------------------------------
// Every C0 control char (0x00-0x1F) plus DEL - deliberately no carve-out for
// tab/CR/LF: these are single-line consumer text fields, and CR/LF in
// particular must never reach an email header unescaped (§31.4 anti-abuse
// item 6, subject-injection guard).
const CONTROL_CHAR_RE = /[\x00-\x1F\x7F]/;
function safeText(max, min = 0) {
    return z
        .string()
        .trim()
        .min(min)
        .max(max)
        .refine((v) => !CONTROL_CHAR_RE.test(v), 'Contains invalid control characters');
}
function optionalSafeText(max) {
    return safeText(max).optional();
}
// ---------------------------------------------------------------------------
// I-31-2 contact validator (P-31-2): name + at least one of (email, phone).
// Phone is permissive - digits are extracted for the length check but
// `phoneRaw` is passed through unmutated (never reformatted/guessed at
// E.164), matching §31.5 step 6's explicit "no aggressive E.164 guessing".
// ---------------------------------------------------------------------------
export const embedContactSchema = z
    .object({
    name: safeText(80, 2),
    email: z.string().trim().toLowerCase().email().max(254).optional(),
    phone: safeText(30, 1).optional(),
    // A2P 10DLC requires an explicit, unchecked-by-default SMS opt-in
    // whenever a phone number is collected for messaging (see the widget's
    // ContactStep for the exact consent text this checkbox shows).
    smsConsent: z.boolean().optional().default(false),
})
    .transform((val) => {
    const phoneDigits = val.phone ? val.phone.replace(/\D/g, '') : '';
    return {
        name: val.name,
        email: val.email ?? null,
        phoneRaw: val.phone ?? null,
        phoneDigits: phoneDigits.length > 0 ? phoneDigits : null,
        smsConsent: val.smsConsent,
    };
})
    .superRefine((val, ctx) => {
    if (!val.email && !val.phoneDigits) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Add an email or a phone number, whichever you prefer',
            path: ['email'],
        });
    }
    if (val.phoneRaw !== null && (val.phoneDigits === null || val.phoneDigits.length < 7 || val.phoneDigits.length > 15)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Phone number must have 7 to 15 digits', path: ['phone'] });
    }
    // Note: smsConsent is deliberately NOT required alongside a phone number.
    // A2P 10DLC carrier vetting requires SMS opt-in to be an optional,
    // standalone choice ("consent is not a condition of purchase"); a phone
    // number without consent just means the shop cannot text the shopper.
});
// ---------------------------------------------------------------------------
// Vehicle / coverage / material / photo (§31.5 steps 1-2-5).
// ---------------------------------------------------------------------------
export const embedVehicleInputSchema = z.object({
    year: z.number().int().min(MIN_VEHICLE_YEAR).refine((y) => y <= maxVehicleYear(), { message: 'Enter a valid model year' }).nullable(),
    make: safeText(60, 1),
    model: safeText(60, 1),
    trim: optionalSafeText(60),
    catalogVehicleId: uuidSchema.optional(),
    catalogTrimId: uuidSchema.optional(),
    matched: z.boolean(),
});
export const embedCoverageInputSchema = z.object({
    type: z.enum(['full', 'partial', 'unsure']),
    areas: z.array(z.enum(EMBED_PANEL_AREAS)).max(EMBED_PANEL_AREAS.length).optional(),
    otherAreaText: optionalSafeText(200),
});
export const embedMaterialInputSchema = z.object({
    finishFamily: z.enum(FINISH_FAMILIES),
    brandPreference: optionalSafeText(80),
    unsure: z.boolean(),
});
export const embedPhotoSchema = z.object({
    objectKey: z.string().trim().min(1).max(500),
    bytes: z.number().int().min(1).max(4 * 1024 * 1024),
    width: z.number().int().min(1),
    height: z.number().int().min(1),
});
// ---------------------------------------------------------------------------
// Phase 31-F per-service question blocks (ppf/tint/ceramic). Wrap's own
// questions stay inline on the wrap branch below, unchanged from Phase 31-A -
// only these three are new.
// ---------------------------------------------------------------------------
export const embedPpfInputSchema = z.object({
    coverage: z.enum(PPF_COVERAGE_CHOICES),
    finish: z.enum(PPF_FINISH_CHOICES),
    customAreaText: optionalSafeText(200),
});
export const embedTintInputSchema = z.object({
    film: z.enum(TINT_FILM_CHOICES),
    shade: z.enum(TINT_SHADE_CHOICES),
    areas: z.array(z.enum(TINT_AREA_CHOICES)).min(1),
    otherAreaText: optionalSafeText(200),
    existing: z.enum(TINT_EXISTING_CHOICES),
});
export const embedCeramicInputSchema = z.object({
    package: z.enum(CERAMIC_PACKAGE_CHOICES),
    correction: z.enum(CERAMIC_CORRECTION_CHOICES),
    addons: z.array(z.enum(CERAMIC_ADDON_CHOICES)).default([]),
});
export const embedPrintInputSchema = z.object({
    coverage: z.enum(PRINT_COVERAGE_CHOICES),
    artwork: z.enum(PRINT_ARTWORK_CHOICES),
    artworkPhoto: embedPhotoSchema.nullable().default(null),
});
// ---------------------------------------------------------------------------
// The full domain payload - what `embed_submissions` stores (§31.3), and
// what P-31-3's round-trip property test fuzzes. Phase 31-F widens this from
// wrap-only to one branch per QUOTE_SERVICES entry, discriminated on the new
// `service` field.
//
// Backward compatibility: every widget build already deployed to a visitor's
// browser cache, and every `embed_submissions` row stored before this
// change, has no `service` key at all. zod v4's discriminatedUnion requires
// the discriminator to actually be present to pick a branch - it can't fall
// back to a default the way a plain object schema's field can - so a bare
// `z.discriminatedUnion('service', [...])` would reject every legacy
// payload. Instead, a preprocess step fills in `service: 'wrap'` before the
// union ever sees the payload, so an absent key and an explicit
// `service: 'wrap'` are indistinguishable to the rest of the schema.
// ---------------------------------------------------------------------------
const embedWrapSubmissionSchema = z.object({
    service: z.literal('wrap'),
    vehicle: embedVehicleInputSchema,
    coverage: embedCoverageInputSchema,
    material: embedMaterialInputSchema,
    disassembly: z.enum(EMBED_DISASSEMBLY_CHOICES),
    condition: z.enum(EMBED_CONDITION_CHOICES),
    removal: z.enum(REMOVAL_TIERS),
    photos: z.array(embedPhotoSchema).max(6).default([]),
    contact: embedContactSchema,
    message: optionalSafeText(1000),
});
const embedPpfSubmissionSchema = z.object({
    service: z.literal('ppf'),
    vehicle: embedVehicleInputSchema,
    ppf: embedPpfInputSchema,
    condition: z.enum(EMBED_CONDITION_CHOICES),
    removal: z.enum(REMOVAL_TIERS),
    photos: z.array(embedPhotoSchema).max(6).default([]),
    contact: embedContactSchema,
    message: optionalSafeText(1000),
});
const embedTintSubmissionSchema = z.object({
    service: z.literal('tint'),
    vehicle: embedVehicleInputSchema,
    tint: embedTintInputSchema,
    photos: z.array(embedPhotoSchema).max(6).default([]),
    contact: embedContactSchema,
    message: optionalSafeText(1000),
});
const embedCeramicSubmissionSchema = z.object({
    service: z.literal('ceramic'),
    vehicle: embedVehicleInputSchema,
    ceramic: embedCeramicInputSchema,
    condition: z.enum(EMBED_CONDITION_CHOICES),
    photos: z.array(embedPhotoSchema).max(6).default([]),
    contact: embedContactSchema,
    message: optionalSafeText(1000),
});
const embedPrintSubmissionSchema = z.object({
    service: z.literal('print'),
    vehicle: embedVehicleInputSchema,
    print: embedPrintInputSchema,
    condition: z.enum(EMBED_CONDITION_CHOICES),
    removal: z.enum(REMOVAL_TIERS),
    photos: z.array(embedPhotoSchema).max(6).default([]),
    contact: embedContactSchema,
    message: optionalSafeText(1000),
});
export const embedSubmissionPayloadSchema = z.preprocess((val) => (typeof val === 'object' && val !== null && !('service' in val) ? { ...val, service: 'wrap' } : val), z.discriminatedUnion('service', [
    embedWrapSubmissionSchema,
    embedPpfSubmissionSchema,
    embedTintSubmissionSchema,
    embedCeramicSubmissionSchema,
    embedPrintSubmissionSchema,
]));
// ---------------------------------------------------------------------------
// Multi-service quotes: a customer picks one or more of wrap/ppf/tint/ceramic
// in a single request and answers each selected service's own questions.
// The per-service branches above (embedWrapSubmissionSchema etc.) each
// duplicate vehicle/photos/contact/message alongside their own spec fields,
// which made sense when a submission was always exactly one service. A
// multi-service request asks those shared fields once at the top level and
// keeps only each branch's own spec fields (coverage/material/... for wrap,
// ppf, tint, ceramic) in the `services` array, via `.omit()` on the existing
// branches - the branches themselves stay untouched. `condition` deliberately
// stays on the wrap/ppf/ceramic spec, not hoisted to the top level: the
// widget asks it once and copies the same answer into each selected spec's
// `condition` field, so every array entry is still self-contained.
//
// Backwards compatibility: every already-deployed widget build, and every
// row already stored in `embed_submissions`, sends the legacy single-service
// shape (vehicle/photos/contact/message plus `service` + that service's own
// fields, all at the top level - including the pre-31-F shape with no
// `service` key at all, which defaults to 'wrap'). A preprocess step lifts
// that legacy shape into a one-element `services` array before the real
// schema ever sees it, so every downstream consumer only has to handle the
// array form.
// ---------------------------------------------------------------------------
const SHARED_SUBMISSION_KEYS = ['vehicle', 'photos', 'contact', 'message'];
const embedWrapSpecSchema = embedWrapSubmissionSchema.omit({ vehicle: true, photos: true, contact: true, message: true });
const embedPpfSpecSchema = embedPpfSubmissionSchema.omit({ vehicle: true, photos: true, contact: true, message: true });
const embedTintSpecSchema = embedTintSubmissionSchema.omit({ vehicle: true, photos: true, contact: true, message: true });
const embedCeramicSpecSchema = embedCeramicSubmissionSchema.omit({ vehicle: true, photos: true, contact: true, message: true });
const embedPrintSpecSchema = embedPrintSubmissionSchema.omit({ vehicle: true, photos: true, contact: true, message: true });
const embedServiceSpecSchema = z.discriminatedUnion('service', [
    embedWrapSpecSchema,
    embedPpfSpecSchema,
    embedTintSpecSchema,
    embedCeramicSpecSchema,
    embedPrintSpecSchema,
]);
const embedServicesArraySchema = z
    .array(embedServiceSpecSchema)
    .min(1)
    .max(QUOTE_SERVICES.length)
    .superRefine((services, ctx) => {
    const seen = new Set();
    for (const entry of services) {
        if (seen.has(entry.service)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Duplicate service in request: ${entry.service}` });
        }
        seen.add(entry.service);
    }
});
// Lifts a legacy single-service body (shared fields + `service` + that
// service's own spec fields, all at the top level - or no `service` key at
// all) into a one-element `services` array. A body that already has a
// `services` array passes through untouched.
function liftLegacySingleService(val) {
    if (typeof val !== 'object' || val === null || Array.isArray(val))
        return val;
    if ('services' in val)
        return val;
    const obj = val;
    const service = 'service' in obj ? obj.service : 'wrap';
    const shared = {};
    const spec = { service };
    for (const key of Object.keys(obj)) {
        if (key === 'service')
            continue;
        if (SHARED_SUBMISSION_KEYS.includes(key)) {
            shared[key] = obj[key];
        }
        else {
            spec[key] = obj[key];
        }
    }
    return { ...shared, services: [spec] };
}
export const embedMultiServiceSubmissionSchema = z.preprocess(liftLegacySingleService, z.object({
    vehicle: embedVehicleInputSchema,
    services: embedServicesArraySchema,
    photos: z.array(embedPhotoSchema).max(6).default([]),
    contact: embedContactSchema,
    message: optionalSafeText(1000),
}));
// ---------------------------------------------------------------------------
// §31.4 public API route contracts (Phase 31-B). These wrap the Phase 31-A
// domain schemas above with the transport/anti-abuse envelope and the
// account-less setup/management flow.
// ---------------------------------------------------------------------------
export const embedThemeSchema = z.object({
    accent: z
        .string()
        .trim()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Accent must be a 6-digit hex color')
        .default('#FF4D00'),
    mode: z.enum(['light', 'dark', 'auto']).default('auto'),
});
export const embedWidgetCreateSchema = z.object({
    shopName: safeText(80, 2),
    destinationEmail: z.string().trim().toLowerCase().email().max(254),
    theme: embedThemeSchema.partial().optional(),
    // Which services this widget's coverage/material step should ask about
    // (§31-F). Required on create - there is no sensible default now that
    // wrap is one of four options rather than the only one.
    services: z.array(z.enum(QUOTE_SERVICES)).min(1),
});
export const embedVerifyCodeSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
    code: z
        .string()
        .trim()
        .regex(/^\d{8}$/, 'Code must be 8 digits'),
    // Only meaningful when the email matches more than one workspace (D-31-9):
    // a first call without this returns `needs_workspace_choice`; a second call
    // supplies it to complete the link.
    workspaceId: uuidSchema.optional(),
});
export const embedWorkspaceChoiceSchema = z.object({ id: uuidSchema, name: z.string() });
export const embedVerifyResponseSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('verified'),
        embedKey: z.string(),
        manageToken: z.string(),
        linked: z.object({ workspaceId: uuidSchema, workspaceName: z.string() }).nullable(),
    }),
    z.object({
        status: z.literal('needs_workspace_choice'),
        workspaces: z.array(embedWorkspaceChoiceSchema),
    }),
]);
export const embedResendCodeSchema = z.object({ email: z.string().trim().toLowerCase().email().max(254) });
export const embedWidgetConfigResponseSchema = z.object({
    shopName: z.string(),
    theme: embedThemeSchema,
    logoUrl: z.string().nullable(),
    status: z.enum(['pending', 'active', 'paused', 'disabled']),
    enumBundleVersion: z.number().int(),
    wst: z.string(),
    // Not secret - exposed so the widget can self-check its own host page
    // against the shop's "Allowed domains" setting before rendering the form
    // (server/src/modules/embed/service.ts's createSubmission enforces the
    // same rule on submit, so this is a UX pre-check, not the only gate).
    allowedDomains: z.array(z.string()).nullable(),
    // §31-F: which services this widget's coverage/material step should ask
    // about - the widget shows exactly what the shop offers, never more.
    services: z.array(z.enum(QUOTE_SERVICES)),
});
export const embedCatalogQuerySchema = z.object({
    query: z.string().trim().max(80).optional(),
    make: z.string().trim().max(60).optional(),
});
export const embedUploadRequestSchema = z.object({
    contentType: z.enum(['image/jpeg', 'image/png']),
    bytes: z.number().int().min(1).max(4 * 1024 * 1024),
});
// Shop-logo upload (§31.4, docs/DECISIONS.md ~1429). Logos are small - a
// 1 MB cap instead of the 4 MB photo cap above - and png is allowed since
// shop logos are commonly transparent-background artwork.
export const embedLogoUploadRequestSchema = z.object({
    contentType: z.enum(['image/jpeg', 'image/png']),
    bytes: z.number().int().min(1).max(1 * 1024 * 1024),
});
export const embedUploadResponseSchema = z.object({
    uploadUrl: z.string(),
    objectKey: z.string(),
    expiresAt: z.string(),
});
// The transport/anti-abuse envelope fields (§31.4 #7): a client-generated
// idempotency key, an honeypot field real users never see or fill, and an
// optional Turnstile token (absent/ignored when EMBED_TURNSTILE_ENABLED=false).
const embedSubmissionEnvelopeShape = {
    submissionId: uuidSchema,
    website: z.string().max(200).optional(),
    turnstileToken: z.string().max(4000).optional(),
    // The host page's origin, resolved client-side (window.location
    // .ancestorOrigins / document.referrer, normalised to a host string) - NOT
    // the request's Referer header, which is the widget iframe's own origin,
    // not the parent page's (that bug is what made "Allowed domains" a no-op).
    // Nullish rather than required: an omitted field and an explicit null are
    // both "could not determine it", and both fail closed in createSubmission
    // when allowedDomains is set. Rejecting the omitted case at the schema
    // layer instead would 400 any submission from a widget page loaded just
    // before a deploy, turning a deploy window into lost leads for no security
    // gain. Client-supplied either way, so this is a deterrent against casual
    // snippet copying, not a security boundary.
    parentOrigin: z.string().trim().max(253).nullish(),
};
// The full anonymous submission request - the Phase 31-A/F domain payload
// plus the envelope above. `.extend()` doesn't exist on
// embedSubmissionPayloadSchema any more (it's a preprocess-wrapped
// discriminated union, not a plain object schema, now that Phase 31-F added
// per-service branches), so the envelope is applied to each branch here and
// the same "fill in service: 'wrap' when absent" preprocess is repeated
// around the resulting union.
export const embedSubmissionRequestSchema = z.preprocess((val) => (typeof val === 'object' && val !== null && !('service' in val) ? { ...val, service: 'wrap' } : val), z.discriminatedUnion('service', [
    embedWrapSubmissionSchema.extend(embedSubmissionEnvelopeShape),
    embedPpfSubmissionSchema.extend(embedSubmissionEnvelopeShape),
    embedTintSubmissionSchema.extend(embedSubmissionEnvelopeShape),
    embedCeramicSubmissionSchema.extend(embedSubmissionEnvelopeShape),
    embedPrintSubmissionSchema.extend(embedSubmissionEnvelopeShape),
]));
export const embedEventSchema = z.object({ type: z.literal('start') });
// ---------------------------------------------------------------------------
// Account-less management (§31.4 #9-11, §31.9).
// ---------------------------------------------------------------------------
export const embedManagePatchSchema = z.object({
    shopName: safeText(80, 2).optional(),
    theme: embedThemeSchema.partial().optional(),
    allowedDomains: z.array(z.string().trim().max(253)).max(20).optional(),
    status: z.enum(['active', 'paused']).optional(),
    // null clears the logo. A string must be a logo-upload objectKey for this
    // widget (enforced server-side, not here - the schema doesn't know the
    // widget id).
    logoObjectKey: z.string().max(300).nullable().optional(),
    services: z.array(z.enum(QUOTE_SERVICES)).min(1).optional(),
});
export const embedManageResponseSchema = z.object({
    embedKey: z.string(),
    shopName: z.string(),
    destinationEmail: z.string(),
    theme: embedThemeSchema,
    allowedDomains: z.array(z.string()).nullable(),
    logoUrl: z.string().nullable(),
    status: z.enum(['pending', 'active', 'paused', 'disabled']),
    linked: z.object({ workspaceId: uuidSchema, workspaceName: z.string() }).nullable(),
    viewsCount: z.number().int(),
    startsCount: z.number().int(),
    submissionsCount: z.number().int(),
    lastSubmissionAt: z.string().nullable(),
    createdAt: z.string(),
    // §31-F: mirrors embedWidgetConfigResponseSchema's `services` - the manage
    // UI needs to show/edit which services this widget currently asks about.
    services: z.array(z.enum(QUOTE_SERVICES)),
});
export const embedChangeEmailSchema = z.object({ email: z.string().trim().toLowerCase().email().max(254) });
export const embedRegenerateKeyResponseSchema = z.object({ embedKey: z.string() });
//# sourceMappingURL=embed.js.map